import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { McAuthType } from '@apiary/shared'
import type { CreateMcAccountRequest, McAccountDto, UpdateMcAccountRequest } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { useDraft } from '@/ui/hooks/useDraft'
import { Button } from '@/ui/primitives/Button'
import { Dialog } from '@/ui/primitives/Dialog'
import { TextField } from '@/ui/primitives/Field'
import { Select } from '@/ui/primitives/Select'
import { fieldErrorsOf, problemText } from '../shared/problem'

/**
 * Один диалог на создание и на правку Minecraft-аккаунта.
 *
 * Почему один, а не два: набор полей совпадает на три четверти, и две копии
 * разъехались бы при первой же новой настройке аккаунта. Отличия сведены к двум
 * правилам:
 *
 *  - тип авторизации выбирается ТОЛЬКО при создании: сменить его у живого
 *    аккаунта — это другой аккаунт, у него другой UUID и другие токены;
 *  - ник MICROSOFT-аккаунта приходит из профиля Mojang и правке не подлежит
 *    (показываем только для чтения), а ник OFFLINE — просто строка, которой бот
 *    представляется, и её переименовать иногда нужно. Пока аккаунт занят
 *    запущенным ботом, ник не меняем: бот уже вошёл под старым.
 *
 * Поля держит `useDraft`, а не `useEffect(() => setLabel(account.label), […])`:
 * статус аккаунта прилетает по WS (`mcAccount.status`), после `READY` список
 * ещё и перечитывается — и прежний вариант затирал набранное название прямо во
 * время ввода.
 */

export interface AccountDialogProps {
  open: boolean
  /** `null` — создаём новый аккаунт. */
  account: McAccountDto | null
  onClose: () => void
  /** Созданному MICROSOFT-аккаунту нужен device-code: экран открывает его сам. */
  onNeedsAuth: (accountId: string) => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const USERNAME_RE = /^[A-Za-z0-9_]{3,16}$/

interface AccountFormState {
  label: string
  authType: McAuthType
  msaEmail: string
  username: string
}

function formOf(account: McAccountDto | null): AccountFormState {
  return {
    label: account?.label ?? '',
    authType: account?.authType ?? 'MICROSOFT',
    msaEmail: account?.msaEmail ?? '',
    username: account?.gamertag ?? ''
  }
}

export function AccountDialog({
  open,
  account,
  onClose,
  onNeedsAuth
}: AccountDialogProps): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)
  const editing = account !== null

  // Метка формы: какую сущность открыли. Закрыли окно или открыли соседний
  // аккаунт — черновик начинается заново; фоновое обновление того же аккаунта
  // метку не меняет и введённое не трогает.
  const seed = open ? (account?.id ?? 'new') : null
  const draft = useDraft(formOf(account), seed)
  const { label, authType, msaEmail, username } = draft.value
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  // Ошибки прошлой попытки — только по смене метки: зависеть от самого объекта
  // аккаунта здесь нельзя, он меняется в фоне.
  useEffect(() => {
    setErrors({})
    setFormError(null)
  }, [seed])

  const locked = account !== null && account.lockedByBotId !== null
  const offline = authType === 'OFFLINE'

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    if (label.trim() === '') found.label = 'Без названия аккаунт не отличить от соседнего'
    if (!offline && msaEmail.trim() !== '' && !EMAIL_RE.test(msaEmail.trim())) {
      found.msaEmail = 'Похоже, это не адрес почты'
    }
    if (offline && !editing && !USERNAME_RE.test(username.trim())) {
      found.username = 'Ник Minecraft: 3–16 символов, латиница, цифры и «_»'
    }
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const onFailure = (error: unknown): void => {
    const fields = fieldErrorsOf(error)
    if (Object.keys(fields).length > 0) setErrors(fields)
    setFormError(problemText(error))
  }

  const create = useMutation({
    mutationFn: (dto: CreateMcAccountRequest) => api.mcAccounts.create(dto),
    onSuccess: created => {
      void queryClient.invalidateQueries({ queryKey: qk.mcAccounts.list })
      push({
        message: `Аккаунт «${created.label}» добавлен`,
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      onClose()
      // MICROSOFT без device-code бесполезен: сразу ведём пользователя за кодом.
      if (created.authType === 'MICROSOFT') onNeedsAuth(created.id)
    },
    onError: onFailure
  })

  const save = useMutation({
    mutationFn: (dto: UpdateMcAccountRequest) => {
      if (account === null) throw new Error('нет аккаунта для правки')
      return api.mcAccounts.update(account.id, dto)
    },
    onSuccess: updated => {
      void queryClient.invalidateQueries({ queryKey: qk.mcAccounts.list })
      // Карточка бота показывает метку аккаунта — без этого там осталась бы старая.
      void queryClient.invalidateQueries({ queryKey: qk.bots.root })
      push({
        message: `Аккаунт «${updated.label}» сохранён`,
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      onClose()
    },
    onError: onFailure
  })

  const submit = (): void => {
    setFormError(null)
    if (!validate()) return

    if (account === null) {
      create.mutate({
        label: label.trim(),
        authType,
        ...(offline ? { username: username.trim() } : {}),
        ...(msaEmail.trim() === '' || offline ? {} : { msaEmail: msaEmail.trim() })
      })
      return
    }

    const dto: UpdateMcAccountRequest = {
      ...(label.trim() === account.label ? {} : { label: label.trim() }),
      // Пустая строка = «стереть почту»: сервер отличает '' от отсутствия поля.
      ...(offline || msaEmail.trim() === (account.msaEmail ?? '')
        ? {}
        : { msaEmail: msaEmail.trim() }),
      ...(offline && !locked && username.trim() !== (account.gamertag ?? '')
        ? { username: username.trim() }
        : {})
    }
    if (Object.keys(dto).length === 0) {
      onClose()
      return
    }
    save.mutate(dto)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={editing ? `Аккаунт «${account.label}»` : 'Новый Minecraft-аккаунт'}
      note={
        editing
          ? 'Название и почта — только для вашего удобства, бот их не видит'
          : 'После создания Microsoft-аккаунт попросит код входа'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={create.isPending || save.isPending} onClick={submit}>
            {editing ? 'Сохранить' : 'Создать'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="Название"
          hint="Как аккаунт будет называться в списках: «main», «ферма», «альт»"
          value={label}
          required
          autoFocus
          error={errors.label ?? null}
          onBlur={() => validate()}
          onChange={event => draft.patch({ label: event.target.value })}
        />

        {editing ? (
          <TextField
            label="Тип авторизации"
            hint="Тип задаётся при создании: у другого типа был бы другой аккаунт"
            value={offline ? 'Offline (без Mojang)' : 'Microsoft'}
            readOnly
          />
        ) : (
          <Select
            label="Тип авторизации"
            value={authType}
            onValueChange={value =>
              draft.patch({ authType: value === 'OFFLINE' ? 'OFFLINE' : 'MICROSOFT' })
            }
            options={[
              { value: 'MICROSOFT', label: 'Microsoft', note: 'лицензия' },
              { value: 'OFFLINE', label: 'Offline', note: 'свой сервер' }
            ]}
            hint={
              offline
                ? 'Подойдёт только серверу с выключенной проверкой лицензии'
                : 'Вход по коду устройства: пароль Microsoft панель не спрашивает'
            }
          />
        )}

        {offline ? (
          <TextField
            label="Ник в игре"
            mono
            hint={
              locked
                ? 'Аккаунт занят запущенным ботом: бот уже вошёл под старым ником'
                : 'Именно этим ником бот представится серверу'
            }
            value={username}
            disabled={locked}
            error={errors.username ?? null}
            onBlur={() => validate()}
            onChange={event => draft.patch({ username: event.target.value })}
          />
        ) : (
          <>
            <TextField
              label="Почта Microsoft"
              type="email"
              mono
              autoComplete="off"
              hint="Панель не может узнать её сама — device-code не возвращает адрес"
              value={msaEmail}
              error={errors.msaEmail ?? null}
              onBlur={() => validate()}
              onChange={event => draft.patch({ msaEmail: event.target.value })}
            />
            {editing ? (
              <>
                <TextField
                  label="Ник Minecraft"
                  hint="Приходит из профиля Mojang после входа"
                  value={account.gamertag ?? '—'}
                  mono
                  readOnly
                />
                <TextField label="UUID" value={account.mcUuid ?? '—'} mono readOnly />
              </>
            ) : null}
          </>
        )}

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}
      </div>
    </Dialog>
  )
}
